import {
  ProductSubscriptionModel,
  ProductSubscriptionStatusesEnum,
  RecurringTypesEnum,
} from "dash-common";
import {
  Badge,
  Button,
  Icon,
  ModalContext,
  Text,
  useCheckIfSubscriptionFeatureActiveBroker,
  ViewerContext,
} from "dash-ui";
import {
  SubscriptionProduct,
  useCanUserTrialForProductQuery,
} from "gql/generated.graphql";
import { LinkToCustomerPortal } from "pages/ProfilePage/components/ProfileBilling/components";
import { getFeatureLabels } from "pages/ProfilePage/components/ProfileBilling/components/SubscriptionProductsSection/components/ProductCardItem/ProductCardItem.helpers";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { ISubscriptionProductCardProps } from "./SubscriptionProductCard.interfaces";
import moment from "moment";

function ProductFeatures({ featuresList, product }) {
  return featuresList.map((feature, index) => {
    return (
      <div className="flex mt-2 flex-grow text-sm" key={index}>
        <div>
          <Icon color="white" size={14} name="check" mr={2} mt={0.5} />
        </div>
        <div
          className={`text-white ${
            index === 0 && product.price > 0 ? "font-bold" : ""
          }`}
        >
          {feature}
        </div>
      </div>
    );
  });
}

function NewProductBadge() {
  return (
    <div className="absolute top-0 right-0 rounded-tr-md rounded-bl-lg font-semibold bg-white text-sky-600 py-1 px-2 text-sm flex justify-center items-center">
      NEW
    </div>
  );
}

function ProductPrice({
  product,
  animated,
}: {
  product: SubscriptionProduct;
  animated?: boolean;
}) {
  const { t } = useTranslation("pages");

  return (
    <div className="flex flex-row">
      {product.price !== 0 && (
        <span className="text-xl text-slate-300 pr-1">$</span>
      )}

      <span className="text-5xl m-0 leading-none font-bold">
        {getPricePerMonth(product).toFixed(2)}
      </span>

      {product.price > 0 && (
        <div className="text-xs leading-none mb-2 ml-1 self-end">
          <p>USD</p>
          <p>/{t(`common:subscriptionProductRecurringLabels:month`)}</p>
        </div>
      )}

      {product.recurringType === RecurringTypesEnum.SIXMONTH && (
        <div className="self-center items-center ml-4">
          <Badge
            square
            title="Save 10%!"
            type="neutralSolid"
            className={`h-fit ${
              animated ? "transition-all animate-bounce" : ""
            }`}
          />
        </div>
      )}
    </div>
  );
}

function ProductStatus({
  subscription,
}: {
  subscription: ProductSubscriptionModel | null;
}) {
  const { status } = subscription || {};
  const { ACTIVE, TRIALING, SCHEDULED_CANCELLATION } =
    ProductSubscriptionStatusesEnum;

  const isTrialing = status === TRIALING;
  const isScheduledCancellation = status === SCHEDULED_CANCELLATION;
  const isActive = status === ACTIVE;

  if (!subscription) return null;

  return (
    <>
      {isActive && (
        <div className="flex flex-row items-center justify-center gap-1 absolute top-0 self-center bg-green-50 text-green-600 text-xs font-semibold rounded-br-lg rounded-bl-lg px-2 py-1">
          <Icon name="checkCircle" /> Current plan
        </div>
      )}
      {isScheduledCancellation && (
        <div className="flex flex-row items-center justify-center gap-1 absolute top-0 self-center bg-red-50 text-red-600 text-xs font-semibold rounded-br-lg rounded-bl-lg px-2 py-1">
          <Icon name="calendarToday" /> Cancels on{" "}
          {moment(
            subscription.schedule?.nextPhaseStart ??
              subscription.currentPeriodEndAt
          ).format("MMM DD")}
        </div>
      )}
      {isTrialing && (
        <div className="flex flex-row items-center justify-center gap-2 absolute top-0 self-center bg-amber-50 text-amber-600 text-xs font-semibold rounded-br-lg rounded-bl-lg px-2 py-1">
          <Icon name="calendarToday" /> Trial ends{" "}
          {moment(subscription.trialEndAt).format("MMM DD")}
        </div>
      )}
    </>
  );
}

export default function SubscriptionProductCard({
  product,
  isCurrent,
  onSelectPlan,
  loading,
  animated,
}: ISubscriptionProductCardProps) {
  const { t, i18n } = useTranslation(["pages", "common"]);

  const { subscription } = useContext(ViewerContext);
  const { subscriptionProduct: userProduct, status } = subscription || {};
  const { ACTIVE, TRIALING } = ProductSubscriptionStatusesEnum;
  const isTrialing = status === TRIALING;
  const isActive = status === ACTIVE;

  const { toggleModal } = useContext(ModalContext);

  const { data: canUserTrialForProductData } = useCanUserTrialForProductQuery({
    variables: {
      input: {
        stripeProductId: product.stripeProductId || "",
      },
    },
  });
  const canUserTrial =
    canUserTrialForProductData?.canUserTrialForProduct && !isTrialing;

  const gtmProductTag = `gtm-${product?.name
    ?.toLowerCase()
    .split(" ")
    .join("-")}`;

  const featuresList = getFeatureLabels(product, {
    t,
    i18n,
  });

  function handleCancelTrial() {
    toggleModal(true, "CancelTrialModal");
  }

  function getBadgeTitle() {
    if (product.price === 0) {
      return t("common:subscriptionBadgeTitles.noCreditCard");
    }

    if (canUserTrial && product.trial)
      return t("common:subscriptionBadgeTitles.trialAvailable", {
        trialDays: product.trial,
      });

    const recurringTitles = {
      [RecurringTypesEnum.MONTH]: t(
        "common:subscriptionBadgeTitles.saveWithSixMonthPlan"
      ),
      [RecurringTypesEnum.SIXMONTH]: t(
        "common:subscriptionBadgeTitles.billedEverySixMonths"
      ),
    };

    return recurringTitles[product.recurringType] || "";
  }

  return (
    <div
      className={`${
        isCurrent ? "outline-sky-600 outline outline-offset-2" : ""
      } relative flex flex-col flex-grow gap-6 p-6 rounded-lg w-full bg-sky-600 text-white max-w-3xl min-w-[300px] shadow-subscriptionCardShadow`}
    >
      {product.name === "DASH Exchange" && <NewProductBadge />}

      {isCurrent && <ProductStatus subscription={subscription} />}

      <div className="card-header flex flex-col gap-2 mt-2">
        <div className="flex flex-row gap-2 items-center">
          <h2 className={`text-2xl text-yellow-400`}>{product.name}</h2>
        </div>

        <ProductPrice product={product} animated={animated} />

        <div className="w-fit">
          <Badge type="neutral" title={getBadgeTitle()}></Badge>
        </div>
      </div>

      {!isCurrent && (
        <div className="relative w-full">
          <Button
            colorVariation="light"
            isLoading={loading}
            disabled={
              (subscription &&
                !isActive &&
                userProduct?.name === "DASH Exchange") ||
              !!subscription?.schedule
            }
            w="100%"
            onClick={() => onSelectPlan(product, canUserTrial)}
            textLabelClassName={gtmProductTag}
            className={gtmProductTag}
          >
            Get {product.name}
          </Button>
          {canUserTrial && !isCurrent && product.trial && (
            <div className="absolute -top-1 -right-3">
              <Badge type="active" title={`${product.trial}-day trial`} />
            </div>
          )}
        </div>
      )}

      {isCurrent && !isTrialing && userProduct?.name !== "DASH Exchange" && (
        <Button disabled>Your current plan</Button>
      )}

      {isCurrent && isTrialing && userProduct?.name === "DASH Exchange" && (
        <Button
          onClick={handleCancelTrial}
          variation="primary"
          colorVariation="danger"
        >
          Cancel trial
        </Button>
      )}

      <div className="flex flex-col flex-grow">
        <ProductFeatures product={product} featuresList={featuresList} />
      </div>

      <LinkToCustomerPortal
        className={`${
          !isCurrent ||
          (isTrialing && userProduct?.name !== "Basic") ||
          product.price === 0
            ? "invisible"
            : ""
        } text-sm text-slate-200 underline`}
      >
        Manage your subscription
      </LinkToCustomerPortal>
    </div>
  );
}

function getPricePerMonth(product?: SubscriptionProduct) {
  if (!product) return 0;
  return (
    product.price /
    (product.recurringType === RecurringTypesEnum.SIXMONTH ? 6 : 1)
  );
}
